import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, Loader2, MapPin, Phone, Mail, Copy, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/** Admin — passagers / conducteurs regroupés par ville. */
export default function UsersByCityTab() {
  const [role, setRole] = useState("clients");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});

  const load = useCallback(async (r) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/price/users-by-city?role=${r}`);
      setData(res.data);
    } catch { toast.error("Chargement impossible"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { setData(null); setOpen({}); load(role); }, [role, load]);

  const zones = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!data) return [];
    if (!t) return data.zones;
    return data.zones
      .map((z) => (z.label.toLowerCase().includes(t) ? z : { ...z, cities: z.cities.filter((c) => c.city.toLowerCase().includes(t)) }))
      .filter((z) => z.cities.length);
  }, [data, q]);

  const copy = (txt, label) => {
    navigator.clipboard?.writeText(txt).then(() => toast.success(`${label} copiés`)).catch(() => toast.error("Copie impossible"));
  };

  return (
    <div className="space-y-3" data-testid="users-by-city-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex overflow-hidden rounded-xl street-border-thin">
          {[["clients", "Passagers"], ["drivers", "Conducteurs"]].map(([k, l]) => (
            <button key={k} onClick={() => setRole(k)} data-testid={`by-city-role-${k}`}
              className={"px-3 py-2 text-[12px] font-black " + (role === k ? "bg-primary text-primary-foreground" : "bg-white")}>{l}</button>
          ))}
        </div>
        <label className="flex flex-1 items-center gap-2 rounded-xl bg-white px-3 py-2 street-border-thin">
          <Search className="h-4 w-4 text-foreground/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ville ou département (93…)" className="w-full bg-transparent text-[13px] font-bold outline-none" data-testid="by-city-search" />
        </label>
      </div>

      {loading && <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />}
      {data && (
        <div className="text-[12px] font-bold text-foreground/60" data-testid="by-city-summary">
          {data.total} {role === "clients" ? "passagers" : "conducteurs"} · {data.with_city} avec ville · {data.zones.filter((z) => z.code !== "ZZ").length} départements
        </div>
      )}

      <div className="space-y-3">
        {zones.map((z) => (
          <ZoneBlock key={z.code} z={z} role={role} open={open} setOpen={setOpen} onCopy={copy} />
        ))}
      </div>
    </div>
  );
}

function ZoneBlock({ z, role, open, setOpen, onCopy }) {
  const all = z.cities.flatMap((c) => c.users);
  const emails = [...new Set(all.map((u) => u.email).filter(Boolean))].join(", ");
  const phones = [...new Set(all.map((u) => u.phone).filter(Boolean))].join(", ");
  const zid = `zone-${z.code}`;
  const isOpen = open[zid] ?? true;
  return (
    <div className="rounded-3xl bg-secondary/30 p-3 street-border-thin" data-testid={`by-zone-${z.code}`}>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setOpen((o) => ({ ...o, [zid]: !isOpen }))} className="flex w-full min-w-0 items-center gap-2 text-left" data-testid={`by-zone-toggle-${z.code}`}>
          <ChevronDown className={"h-4 w-4 flex-none transition-transform " + (isOpen ? "rotate-180" : "")} />
          <span className="truncate font-[Outfit] text-[16px] font-black">{z.label}</span>
          <span className="rounded-full bg-black px-2.5 py-0.5 text-[12px] font-black text-white" data-testid={`by-zone-count-${z.code}`}>{z.count}</span>
          {role === "drivers" && z.online_count > 0 && <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-black text-green-700">{z.online_count} en ligne</span>}
        </button>
        {emails && (
          <button onClick={() => onCopy(emails, `E-mails ${z.label}`)} className="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1.5 text-[11px] font-black text-primary-foreground street-border-thin" data-testid={`by-zone-copy-emails-${z.code}`}>
            <Copy className="h-3 w-3" />Copier les e-mails de la zone
          </button>
        )}
        {phones && (
          <button onClick={() => onCopy(phones, `Téléphones ${z.label}`)} className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-black street-border-thin" data-testid={`by-zone-copy-phones-${z.code}`}>
            <Copy className="h-3 w-3" />Téléphones
          </button>
        )}
      </div>
      {isOpen && (
        <div className="mt-2 space-y-2">
          {z.cities.map((g) => (
            <CityGroup key={g.city} g={g} role={role} open={!!open[g.city]} onToggle={() => setOpen((o) => ({ ...o, [g.city]: !o[g.city] }))} onCopy={onCopy} />
          ))}
        </div>
      )}
    </div>
  );
}

function CityGroup({ g, role, open, onToggle, onCopy }) {
  const slug = g.city.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const phones = g.users.map((u) => u.phone).filter(Boolean).join(", ");
  const emails = g.users.map((u) => u.email).filter(Boolean).join(", ");
  return (
    <div className="rounded-2xl bg-white street-border-thin" data-testid={`by-city-group-${slug}`}>
      <button onClick={onToggle} className="flex w-full items-center gap-3 p-3 text-left" data-testid={`by-city-toggle-${slug}`}>
        <MapPin className="h-4 w-4 flex-none text-primary" />
        <div className="min-w-0 flex-1 truncate font-[Outfit] text-[14px] font-black">{g.city}</div>
        {role === "drivers" && g.online_count > 0 && (
          <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-black text-green-700">{g.online_count} en ligne</span>
        )}
        <span className="rounded-full bg-primary px-2.5 py-0.5 text-[12px] font-black text-primary-foreground" data-testid={`by-city-count-${slug}`}>{g.count}</span>
        <ChevronDown className={"h-4 w-4 transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div className="border-t-2 border-black/10 p-3">
          <div className="mb-2 flex flex-wrap gap-2">
            {phones && <button onClick={() => onCopy(phones, "Téléphones")} className="inline-flex items-center gap-1 rounded-lg bg-secondary/50 px-2 py-1 text-[11px] font-black street-border-thin" data-testid={`by-city-copy-phones-${slug}`}><Copy className="h-3 w-3" />Copier les téléphones</button>}
            {emails && <button onClick={() => onCopy(emails, "E-mails")} className="inline-flex items-center gap-1 rounded-lg bg-secondary/50 px-2 py-1 text-[11px] font-black street-border-thin" data-testid={`by-city-copy-emails-${slug}`}><Copy className="h-3 w-3" />Copier les e-mails</button>}
          </div>
          <ul className="divide-y divide-black/5">
            {g.users.map((u) => (
              <li key={u.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 py-1.5 text-[12px]" data-testid={`by-city-user-${u.id}`}>
                <span className="font-black">{u.name}</span>
                {u.online && <span className="rounded bg-green-100 px-1.5 text-[10px] font-black text-green-700">EN LIGNE</span>}
                {u.dual_role && <span className="rounded bg-accent px-1.5 text-[10px] font-black">DOUBLE RÔLE</span>}
                {u.suspended && <span className="rounded bg-red-100 px-1.5 text-[10px] font-black text-red-700">SUSPENDU</span>}
                {u.phone && <a href={`tel:${u.phone}`} className="inline-flex items-center gap-1 text-foreground/70"><Phone className="h-3 w-3" />{u.phone}</a>}
                {u.email && <a href={`mailto:${u.email}`} className="inline-flex items-center gap-1 text-foreground/70"><Mail className="h-3 w-3" />{u.email}</a>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
